import { useRef, useState } from 'react'
import { InspectionService, ReasonPresetService } from 'utility'
import { useNotification } from 'hooks/toast'
import {
    findExistingReasonNote,
    getReasonPresetErrorMessage,
    noteIdOf,
    rememberReasonNoteId,
    storedReasonNoteId,
} from './utils'
import {
    ChipRow,
    NotesChip,
    NoteField,
    SelectedNotesBox,
    ShowAllButton,
    SourceBadge,
    SuggestLabel,
    SuggestPill,
    PillWrap,
} from './styles'

const sourceColors = source => {
    const key = String(source || '').toLowerCase()
    if (key === 'ai') return { bg: '#ccfbf1', color: '#0f766e' }
    if (key === 'manual') return { bg: '#e2e8f0', color: '#334155' }
    if (key === 'library') return { bg: '#ede9fe', color: '#6d28d9' }
    return { bg: '#ffedd5', color: '#c2410c' }
}

const latestExternalNote = question => {
    const notes = Array.isArray(question?.Notes) ? question.Notes : []
    return [...notes].reverse().find(note => note && !note.IsInternal) || null
}

const sameText = (left, right) =>
    String(left || '').trim().toLowerCase() === String(right || '').trim().toLowerCase()

const noteParts = (presets, question) => {
    const existing =
        latestExternalNote(question) || findExistingReasonNote(question, presets)
    const parts = String(existing?.Notes || '')
        .split(';')
        .map(part => part.trim())
        .filter(Boolean)
    const chips = []
    const extra = []
    parts.forEach(reasonText => {
        const preset = (presets || []).find(item => sameText(item.ReasonText, reasonText))
        if (preset && !chips.some(chip => sameText(chip.ReasonText, reasonText))) {
            chips.push({ ...preset, IsManual: false })
        } else if (!preset) {
            extra.push(reasonText)
        }
    })
    return { chips, extra: extra.join('; ') }
}

export const ReasonPresetNotesPanel = ({
    question,
    presets,
    setting,
    auditId,
    addNotes,
    disabled,
    tabs,
}) => {
    const { errorToast } = useNotification()
    const maxChips = Math.min(20, Math.max(1, Number(setting?.MaxChips) || 8))
    const requireReason = setting?.RequireReason === true
    const initialRef = useRef(null)
    if (!initialRef.current) initialRef.current = noteParts(presets, question)
    const [chips, setChips] = useState(() => initialRef.current.chips.slice(0, maxChips))
    const [extra, setExtra] = useState(initialRef.current.extra)
    const [showAll, setShowAll] = useState(false)
    const savingRef = useRef(false)
    const savedExtraRef = useRef(initialRef.current.extra)
    const fieldRef = useRef(null)

    const persist = async (nextChips, nextExtra = extra) => {
        if (disabled || savingRef.current) return
        const typed = String(nextExtra || '').trim()
        if (requireReason && nextChips.length === 0 && !typed) {
            errorToast('Keep at least one reason.')
            return
        }
        const selections = nextChips.slice(0, 30).map(chip => ({
            PresetId:
                !chip.IsManual && Number.isFinite(Number(chip.Id))
                    ? Number(chip.Id)
                    : null,
            ReasonText: chip.ReasonText,
        }))
        savingRef.current = true
        try {
            const existing =
                latestExternalNote(question) ||
                findExistingReasonNote(question, presets)
            const existingId =
                noteIdOf(existing) || storedReasonNoteId(question?.Id)
            if (selections.length || typed || existingId) {
                const joined = [
                    ...selections.map(item => item.ReasonText),
                    ...(typed ? [typed] : []),
                ].join('; ')
                const payload = {
                    FAQId: question?.Id,
                    IsInternal: false,
                    Notes: joined,
                    ...(existingId ? { NoteId: existingId } : {}),
                }
                let noteResult = null
                if (typeof addNotes === 'function') {
                    noteResult = await addNotes(question, payload)
                    if (noteResult === null) {
                        throw new Error('Could not save the reason note.')
                    }
                } else {
                    noteResult = existingId
                        ? await InspectionService.updateNote([payload])
                        : await InspectionService.addNotes([payload])
                }
                const savedNote = Array.isArray(noteResult)
                    ? noteResult[0]
                    : noteResult
                rememberReasonNoteId(
                    question?.Id,
                    noteIdOf(savedNote) || existingId,
                )
                savedExtraRef.current = typed
            }
            if (auditId != null && question?.Id != null) {
                await ReasonPresetService.saveSelection({
                    AuditId: Number(auditId),
                    AuditQuestionId: Number(question.Id),
                    Selections: selections,
                })
            }
        } catch (error) {
            errorToast(getReasonPresetErrorMessage(error))
        } finally {
            savingRef.current = false
        }
    }

    const addChip = preset => {
        if (disabled) return
        if (chips.some(chip => sameText(chip.ReasonText, preset.ReasonText))) return
        if (chips.length >= maxChips) {
            errorToast(`You can keep up to ${maxChips} reasons.`)
            return
        }
        const next = [...chips, { ...preset, IsManual: false }]
        setChips(next)
        persist(next, extra)
    }

    const removeChip = index => {
        const next = chips.filter((_, itemIndex) => itemIndex !== index)
        if (requireReason && next.length === 0 && !String(extra || '').trim()) {
            errorToast('Keep at least one reason.')
            return
        }
        setChips(next)
        persist(next, extra)
    }

    const available = (presets || []).filter(
        preset => !chips.some(chip => sameText(chip.ReasonText, preset.ReasonText)),
    )
    const visible = showAll ? available : available.slice(0, 3)

    return (
        <div style={{ width: '100%' }}>
            {!disabled && (
                <div>
                    <SuggestLabel>Tap a reason to add it</SuggestLabel>
                    <PillWrap>
                        {visible.map(preset => (
                            <SuggestPill
                                key={preset.Id || preset.ReasonText}
                                type="button"
                                onClick={() => addChip(preset)}
                            >
                                + {preset.ReasonText}
                            </SuggestPill>
                        ))}
                    </PillWrap>
                    {available.length > 3 && (
                        <ShowAllButton
                            type="button"
                            onClick={() => setShowAll(open => !open)}
                        >
                            {showAll
                                ? 'Show fewer'
                                : `Show all ${available.length} reasons`}
                        </ShowAllButton>
                    )}
                </div>
            )}
            <div style={{ marginTop: disabled ? 0 : 10 }}>{tabs}</div>
            <SelectedNotesBox
                onMouseDown={event => {
                    if (disabled || event.target.closest('button')) return
                    if (event.target === fieldRef.current) return
                    event.preventDefault()
                    fieldRef.current?.focus()
                }}
            >
                {chips.length > 0 && (
                    <ChipRow>
                        {chips.map((chip, index) => {
                            const colors = sourceColors(chip.Source)
                            return (
                                <NotesChip key={`${chip.Id || chip.ReasonText}-${index}`}>
                                    {chip.ReasonText}
                                    {chip.Source && (
                                        <SourceBadge $bg={colors.bg} $color={colors.color}>
                                            {chip.Source}
                                        </SourceBadge>
                                    )}
                                    {!disabled && (
                                        <button
                                            type="button"
                                            aria-label="Remove reason"
                                            onClick={() => removeChip(index)}
                                        >
                                            ×
                                        </button>
                                    )}
                                </NotesChip>
                            )
                        })}
                    </ChipRow>
                )}
                <NoteField
                    ref={fieldRef}
                    rows={1}
                    placeholder="Enter Notes"
                    value={extra}
                    readOnly={disabled}
                    disabled={disabled}
                    onChange={event => setExtra(event.target.value)}
                    onBlur={() => {
                        const typed = String(extra || '').trim()
                        if (typed === String(savedExtraRef.current || '').trim()) return
                        persist(chips, typed)
                    }}
                />
            </SelectedNotesBox>
        </div>
    )
}
